import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { notesAPI } from '@/lib/api';
import api, { authHeaders } from '@/lib/api';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  ArrowLeft,
  MessageSquare,
  Plus,
  Camera,
  X,
  Trash2,
  Loader2,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

function getAttachmentUrl(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${window.location.origin}${url}`;
}

async function fetchAttachmentBlobUrl(url) {
  const absoluteUrl = getAttachmentUrl(url);
  const response = await fetch(absoluteUrl, {
    headers: authHeaders(),
  });
  if (!response.ok) throw new Error('Attachment fetch failed');
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

async function compressImage(file, maxPx = 1920, quality = 0.82) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob(blob => resolve(blob || file), 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

export default function MyNotes() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();

  const [feed, setFeed] = useState([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [blobUrls, setBlobUrls] = useState({});

  const [showForm, setShowForm] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const photoInputRef = useRef(null);

  const LIMIT = 10;

  const loadFeed = useCallback(async (startSkip = 0, append = false) => {
    append ? setLoadingMore(true) : setLoading(true);
    try {
      const res = await notesAPI.myFeed(startSkip, LIMIT);
      const items = res.data || [];
      setFeed(prev => append ? [...prev, ...items] : items);
      setSkip(startSkip + items.length);
      setHasMore(items.length === LIMIT);
    } catch {
      // ignore
    } finally {
      append ? setLoadingMore(false) : setLoading(false);
    }
  }, []);

  useEffect(() => { loadFeed(0); }, [loadFeed]);

  useEffect(() => {
    return () => {
      Object.values(blobUrls).forEach((url) => {
        try { URL.revokeObjectURL(url); } catch {}
      });
    };
  }, [blobUrls]);

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
    for (const file of files) {
      if (!allowed.includes(file.type) && !file.name.match(/\.(jpe?g|png|gif|webp|heic|heif)$/i)) {
        toast.error(t('notes.onlyImages') || 'Only images allowed');
        continue;
      }
      if (file.size > 15 * 1024 * 1024) {
        toast.error(t('notes.maxSize') || 'Max 15 MB');
        continue;
      }
      const preview = URL.createObjectURL(file);
      setPhotos(prev => [...prev, { file, preview, name: file.name, takenAt: new Date().toISOString() }]);
    }
    e.target.value = '';
  };

  const removePhoto = (i) => {
    setPhotos(prev => {
      URL.revokeObjectURL(prev[i].preview);
      return prev.filter((_, idx) => idx !== i);
    });
  };

  const submitNote = async () => {
    if (!noteText.trim() && photos.length === 0) return;
    setSubmitting(true);
    try {
      const res = await notesAPI.createSelf({ content: noteText.trim() });
      const noteId = res.data.id;
      for (const photo of photos) {
        try {
          const compressed = await compressImage(photo.file);
          const compressedFile = new File([compressed], photo.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
          await notesAPI.uploadPhoto(noteId, compressedFile, photo.takenAt);
        } catch {
          await notesAPI.delete(noteId).catch(() => {});
          toast.error(t('employee.noteSaveError') || 'Photo upload failed');
          return;
        }
      }
      toast.success(t('employee.noteSaved') || 'Saved');
      setNoteText('');
      setPhotos([]);
      setShowForm(false);
      loadFeed(0);
    } catch {
      toast.error(t('employee.noteSaveError') || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await notesAPI.delete(noteId);
      setFeed(prev => prev.filter(n => n.id !== noteId));
    } catch {
      toast.error('Error');
    }
  };

  const ensureBlobUrl = async (att) => {
    if (!att?.id || !att?.url) return null;
    if (blobUrls[att.id]) return blobUrls[att.id];
    const blobUrl = await fetchAttachmentBlobUrl(att.url);
    setBlobUrls(prev => ({ ...prev, [att.id]: blobUrl }));
    return blobUrl;
  };

  const roleLabel = (role) => role === 'employee' ? t('notes.roleEmployee') : t('notes.roleManager');
  const roleCls = (role) => role === 'employee'
    ? 'bg-emerald-500/20 text-emerald-400'
    : 'bg-wp-medium/30 text-wp-light';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-emerald-400" />
              {t('nav.myNotes') || 'Notes'}
            </h1>
          </div>
          <button
            onClick={() => setShowForm(v => !v)}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-xl border border-emerald-500/30 transition-all text-sm font-medium"
          >
            <Plus className="h-4 w-4" />
            {t('notes.add') || 'Add'}
          </button>
        </div>

        {/* Add note form */}
        {showForm && (
          <div className="glass-card rounded-2xl p-4 space-y-3 border border-emerald-500/20">
            <textarea
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder={t('notes.placeholder') || 'Note text...'}
              rows={3}
              autoFocus
              className="w-full bg-wp-dark/50 border border-wp-dark/60 rounded-xl px-4 py-3 text-white placeholder-white/30 resize-none focus:outline-none focus:border-emerald-500/50 text-sm"
            />

            {photos.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {photos.map((p, i) => (
                  <div key={i} className="relative group">
                    <img src={p.preview} alt={p.name} className="w-20 h-20 object-cover rounded-lg border border-wp-dark/60" />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 rounded-b-lg px-1 py-0.5">
                      <p className="text-[10px] text-white/60 truncate">{format(new Date(p.takenAt), 'dd.MM HH:mm')}</p>
                    </div>
                    <button onClick={() => removePhoto(i)} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-rose-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="h-3 w-3 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <input ref={photoInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoSelect} />
              <button
                onClick={() => photoInputRef.current?.click()}
                className="flex items-center justify-center gap-2 px-3 py-2 bg-wp-dark/50 hover:bg-wp-dark/70 text-wp-light/60 hover:text-white rounded-xl border border-wp-dark/60 transition-all text-sm"
              >
                <Camera className="h-4 w-4" />
                {t('employee.addPhoto') || 'Add photo'}
              </button>
              <div className="flex gap-2 sm:ml-auto">
                <button
                  onClick={() => { setShowForm(false); setNoteText(''); setPhotos([]); }}
                  className="px-4 py-2 text-white/40 hover:text-white text-sm transition-colors"
                >
                  {t('notes.cancel') || 'Cancel'}
                </button>
                <button
                  onClick={submitNote}
                  disabled={submitting || (!noteText.trim() && photos.length === 0)}
                  className="px-5 py-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium rounded-xl text-sm transition-all"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : t('notes.save') || 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Feed */}
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-white/40" />
          </div>
        ) : feed.length === 0 ? (
          <p className="text-white/30 text-sm text-center py-10">{t('notes.noEntries') || 'No notes yet'}</p>
        ) : (
          <div className="space-y-3">
            {feed.map(note => {
              const isOwn = note.author_id === user?.id;
              return (
                <div key={note.id} className="glass-card p-4 rounded-2xl group">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-xs font-medium text-white">{note.author_name}</span>
                    {note.author_role && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${roleCls(note.author_role)}`}>
                        {roleLabel(note.author_role)}
                      </span>
                    )}
                    <span className="text-xs text-white/30 ml-auto">{format(new Date(note.created_at), 'dd.MM.yyyy HH:mm')}</span>
                    {isOwn && (
                      <button onClick={() => deleteNote(note.id)} className="p-1 text-white/20 hover:text-rose-400 transition-colors opacity-0 group-hover:opacity-100">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  {note.content && <p className="text-white text-sm whitespace-pre-wrap">{note.content}</p>}
                  {note.attachments?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {note.attachments.map(att => (
                        <button
                          key={att.id}
                          type="button"
                          onClick={async () => {
                            try {
                              const blobUrl = await ensureBlobUrl(att);
                              if (blobUrl) setPreviewImage(blobUrl);
                            } catch {
                              toast.error(t('notes.imageOpenError') || 'Failed to open image');
                            }
                          }}
                          className="relative group text-left"
                        >
                          <img
                            src={blobUrls[att.id] || getAttachmentUrl(att.url)}
                            alt={att.original_filename}
                            className="w-20 h-20 object-cover rounded-lg border border-wp-dark/60 hover:opacity-80 transition-opacity"
                            onLoadStart={async () => {
                              if (!blobUrls[att.id]) {
                                try { await ensureBlobUrl(att); } catch {}
                              }
                            }}
                            onError={async (e) => {
                              try {
                                const blobUrl = await ensureBlobUrl(att);
                                if (blobUrl) {
                                  e.currentTarget.src = blobUrl;
                                  return;
                                }
                              } catch {}
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.parentElement?.classList.add('w-20', 'h-20', 'rounded-lg', 'border', 'border-wp-dark/60', 'flex', 'items-center', 'justify-center', 'text-[10px]', 'text-white/50', 'break-all', 'p-1');
                              e.currentTarget.parentElement?.insertAdjacentText('beforeend', att.original_filename || 'image');
                            }}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {hasMore && (
              <button
                onClick={() => loadFeed(skip, true)}
                disabled={loadingMore}
                className="w-full py-2 text-sm text-white/40 hover:text-white border border-wp-dark/40 hover:border-wp-dark/70 rounded-xl transition-all"
              >
                {loadingMore ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : t('notes.showMore') || 'Show more'}
              </button>
            )}
          </div>
        )}

        <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
          <DialogContent className="max-w-4xl bg-slate-900 border-wp-dark/50 p-2">
            <DialogTitle className="sr-only">{t('notes.imagePreview') || 'Image preview'}</DialogTitle>
            {previewImage && (
              <img src={previewImage} alt="Preview" className="w-full h-auto max-h-[80vh] object-contain rounded-lg" />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
